// What the two chat surfaces agree about. core.md §9.
//
// Pure functions only — no React, no kit, no `t()`. The desktop and the phone
// render the same conversation in two chromes, and everything that decides
// WHICH rows go where (the sidebar's day buckets, the day dividers in the
// transcript, the four suggested prompts) lives here so the two cannot drift.
//
// Day boundaries are computed in IRAN time, not the browser's: a student
// studying at 01:00 in Berlin is on the Iranian «امروز» that the timestamps
// were stamped in, and grouping in the local zone would file that conversation
// under yesterday for them and today for their classmate.

import { iranDateOf, todayInIran, shiftISO } from '../../lib/iranTime.js';

/** The sidebar's buckets, in the order §9.1 lists them. */
export const DAY_GROUPS = ['today', 'yesterday', 'last7', 'older'];

/** The i18n key for a bucket. */
export const dayGroupKey = (group) => `chat.sidebar.${group}`;

/** Milliseconds in a day, for the 7-day window. */
const DAY_MS = 86400000;

/**
 * Which bucket an instant falls into, relative to `nowMs`.
 * Anything without a usable timestamp is «قدیمی‌تر» rather than «امروز»: an
 * unknown date must never float to the top of the list.
 */
export function dayGroupOf(ms, nowMs = Date.now()) {
  const day = iranDateOf(ms);
  if (!day) return 'older';
  const today = todayInIran(nowMs);
  if (day === today) return 'today';
  if (day === shiftISO(today, -1)) return 'yesterday';
  const n = Number(ms);
  if (Number.isFinite(n) && nowMs - n < 7 * DAY_MS) return 'last7';
  return 'older';
}

/** The instant a thread is sorted and grouped by: its last message, else its birth. */
export const threadAt = (thread) => Number(thread?.lastMessageAtMs || thread?.createdAtMs || 0);

/**
 * Threads → `[{ group, threads }]`, newest bucket first and newest thread first
 * inside each. Empty buckets are dropped: an «۷ روز گذشته» heading over nothing
 * is a heading that says the student lost something.
 */
export function groupThreads(threads, nowMs = Date.now()) {
  const buckets = new Map(DAY_GROUPS.map((g) => [g, []]));
  for (const thread of threads || []) {
    buckets.get(dayGroupOf(threadAt(thread), nowMs)).push(thread);
  }
  return DAY_GROUPS
    .map((group) => ({ group, threads: buckets.get(group).sort((a, b) => threadAt(b) - threadAt(a)) }))
    .filter((bucket) => bucket.threads.length > 0);
}

/**
 * The transcript with a day marker before the first message of each Iranian day.
 * Returns `[{ kind: 'day', day } | { kind: 'message', message }]` so the column
 * renders one flat list and the `ChatDayDivider` decision is made once.
 */
export function withDayDividers(messages) {
  const out = [];
  let day = null;
  for (const message of messages || []) {
    const at = iranDateOf(message?.createdAtMs);
    if (at && at !== day) { day = at; out.push({ kind: 'day', day, key: `day-${at}` }); }
    out.push({ kind: 'message', message, key: message.id });
  }
  return out;
}

/**
 * The id of the LAST assistant message — the only one that carries `AiAnswerNote`
 * (core.md §9.3: once under the latest answer, not under every one).
 */
export function lastAssistantId(messages) {
  for (let i = (messages?.length || 0) - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') return messages[i].id;
  }
  return null;
}

/**
 * The four suggested prompts of §9.1, as `[{ key, params }]` for `t()`. The
 * text a student sees is the catalogue's, and the text SENT is the same string
 * — a suggestion that sent something other than what it read would be a small
 * lie.
 *
 * "Built from the record": when the case has a name and a chief complaint the
 * cards name them — «{name} را خلاصه کن», the differential and the key finding
 * for «{complaint}» — and when nothing is written yet the four static prompts
 * stand in. `myCase` is what the chat screen loads, so `chiefComplaint` is the
 * one clinical field available here; there is no problem list on it.
 */
export const SUGGESTED_PROMPTS = [
  'chat.prompts.summary',
  'chat.prompts.differential',
  'chat.prompts.keyFinding',
  'chat.prompts.nextVisit',
];

export function suggestedPrompts(caseRecord) {
  const name = patientName(caseRecord);
  const complaint = String(caseRecord?.chiefComplaint || '').replace(/\s+/g, ' ').trim();
  return [
    name ? { key: 'chat.prompts.summaryOf', params: { name } } : { key: 'chat.prompts.summary' },
    complaint
      ? { key: 'chat.prompts.differentialFor', params: { complaint } }
      : { key: 'chat.prompts.differential' },
    complaint
      ? { key: 'chat.prompts.keyFindingFor', params: { complaint } }
      : { key: 'chat.prompts.keyFinding' },
    { key: 'chat.prompts.nextVisit' },
  ];
}

/**
 * A sidebar row's time, relative: `{ key, params }` for `t()`, or
 * `{ date: ms }` when it is old enough that a date reads better than "N days
 * ago". Day boundaries are IRAN days like everything else in this file.
 */
export function relativeTimeOf(ms, nowMs = Date.now()) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  const delta = Math.max(0, nowMs - n);
  if (delta < 60_000) return { key: 'chat.sidebar.justNow' };
  if (delta < 3_600_000) return { key: 'chat.sidebar.minutesAgo', params: { count: Math.floor(delta / 60_000) } };
  const group = dayGroupOf(n, nowMs);
  if (group === 'today') return { key: 'chat.sidebar.hoursAgo', params: { count: Math.floor(delta / 3_600_000) } };
  if (group === 'yesterday') return { key: 'chat.sidebar.yesterday' };
  return { date: n };
}

/** The patient's name as the chat header and the empty state say it. */
export const patientName = (row, fallback = '') =>
  String(row?.displayName || row?.title || '').trim() || fallback;

/** The sidebar's open/closed preference. `null` (nothing stored) means OPEN. */
export const sidebarOpenFrom = (stored) => stored !== '0';
export const sidebarOpenTo = (open) => (open ? '1' : '0');
